<div class="group-page">
  <section class="day-summary">
    <div *ngIf="dayLoading()" class="loading">Loading day view…</div>
    <div *ngIf="dayError()" class="lm-notice lm-notice--danger">
      {{ dayError() }}
      <button type="button" class="lm-btn" (click)="refreshDayError()">Refresh day</button>
    </div>
    <div *ngIf="activityReorderError()" class="lm-notice lm-notice--danger">
      {{ activityReorderError() }}
      <button type="button" class="lm-btn" (click)="refreshAllocationError()">Refresh day</button>
    </div>
    <div *ngIf="notThisCycleError()" class="lm-notice lm-notice--danger" role="alert">
      {{ notThisCycleError() }}
    </div>

    <ng-container *ngIf="day()">
      <nav class="group-capsules" aria-label="Activity groups">
        <ul class="lm-tabs lm-tabs--scroll group-capsules-list">
          <li *ngFor="let capsule of workCapsules()">
            <a
              class="lm-tab"
              [class.lm-tab--running]="capsule.running"
              [attr.aria-current]="capsule.ordinal === groupOrdinal() ? 'page' : null"
              [attr.aria-label]="capsule.ariaLabel"
              [routerLink]="groupCommands(capsule.ordinal)">
              <span *ngIf="capsule.running" class="lm-dot" aria-hidden="true"></span>
              {{ capsule.label }}
              <svg *ngIf="capsule.doneThisCycle" lucideCheck class="lm-icon lm-icon--sm" aria-hidden="true"></svg>
            </a>
          </li>

          <li *ngIf="showCapsuleSeparator()" class="lm-tabs__separator" aria-hidden="true"></li>

          <li *ngFor="let capsule of lifeCapsules()">
            <a
              class="lm-tab"
              [class.lm-tab--running]="capsule.running"
              [attr.aria-current]="capsule.ordinal === groupOrdinal() ? 'page' : null"
              [attr.aria-label]="capsule.ariaLabel"
              [routerLink]="groupCommands(capsule.ordinal)">
              <span *ngIf="capsule.running" class="lm-dot" aria-hidden="true"></span>
              {{ capsule.label }}
              <svg *ngIf="capsule.doneThisCycle" lucideCheck class="lm-icon lm-icon--sm" aria-hidden="true"></svg>
            </a>
          </li>
        </ul>
      </nav>

      <header class="group-header" *ngIf="selectedGroup() as group">
        <div class="group-header-main">
          <a
            class="lm-icon-btn"
            routerLink="/today"
            aria-label="Back to Today"
            title="Back to Today">
            <svg lucideArrowLeft class="lm-icon" aria-hidden="true"></svg>
          </a>
          <div class="group-title-copy">
            <h1 class="text-title">{{ resolveScheduleGroupLabel(group) }}</h1>
            <p class="group-meta">{{ groupMetaLine() }}</p>
          </div>
        </div>
        <button
          type="button"
          class="lm-icon-btn lm-icon-btn--ghost"
          [attr.aria-pressed]="dayStatusOldestFirst()"
          aria-label="Oldest first"
          title="Oldest first"
          (click)="toggleOldestFirst()">
          <svg lucideArrowDownWideNarrow class="lm-icon" aria-hidden="true"></svg>
        </button>
      </header>

      <h2 class="lm-sr-only">Activities</h2>

      <section
        *ngIf="showRemoteTimerStrip() && !expandedRemoteTimer() && runningBlock() as runningBlock"
        [id]="runningStripAnchorId"
        class="lm-timer-strip"
        role="status">
        <p class="lm-timer-strip__text">
          <span class="lm-dot" [class.lm-dot--attention]="runningStripAttention()" aria-hidden="true"></span>
          <span class="lm-timer-strip__name">{{ getActivityName(runningBlock.activityTypeId) }}</span>
          <span aria-hidden="true">·</span>
          <a [routerLink]="runningGroupCommands()" [queryParams]="{ timer: 'open' }">in {{ runningGroupLabel() }}</a>
          <span aria-hidden="true">·</span>
          <span class="lm-timer-strip__time" [class.lm-timer-strip__time--attention]="runningStripAttention()">{{ runningStripTimeLabel() }}</span>
        </p>
        <button
          type="button"
          class="lm-icon-btn"
          data-remote-timer-expand
          aria-label="Show timer"
          title="Show timer"
          (click)="expandRemoteTimer()">
          <svg lucideMaximize2 class="lm-icon" aria-hidden="true"></svg>
        </button>
      </section>

      <app-timer-overlay
        *ngIf="showRemoteTimerStrip() && expandedRemoteTimer() && runningBlock() as runningBlock"
        class="remote-running-timer"
        [id]="runningStripAnchorId"
        [runningBlock]="runningBlock"
        [activityName]="getActivityName(runningBlock.activityTypeId)"
        [activityLinkCommands]="['/today/allocation', runningBlock.activityTypeId]"
        [activityLinkQueryParams]="runningActivityQueryParams()"
        [slotProgress]="store.getRunningAllocationProgressLabel()"
        [remainingSlots]="store.getRunningAllocationRemainingSlots()"
        [groupLinkLabel]="runningGroupLabel()"
        [groupLinkCommands]="runningGroupCommands()"
        [submitting]="blockMutationSubmitting()"
        [error]="blockMutationError()"
        (pause)="pauseRunningBlock()"
        (resume)="resumeRunningBlock()"
        (complete)="completeRunningBlock()"
        (autoAdvance)="onAutoAdvance()"
        (skip)="skipRunningBlock($event)"
        (hide)="collapseRemoteTimer()"
        (refreshDayRequest)="refreshTimerError()">
      </app-timer-overlay>

      <p *ngIf="showEmptyMessage()" class="lm-notice group-empty">
        Everything in this group is done or set aside for this cycle.
      </p>

      <div
        class="group-rows"
        cdkDropList
        cdkDropListOrientation="vertical"
        [cdkDropListDisabled]="activityReorderSubmitting()"
        (cdkDropListDropped)="onAllocationDrop($event)">
        <div
          *ngFor="let row of groupRows()"
          class="group-row-item"
          cdkDrag
          cdkDragLockAxis="y"
          [cdkDragStartDelay]="listDragStartDelay"
          [cdkDragDisabled]="activityReorderSubmitting() || row.doneThisCycle || row.notThisCycle">
          <ng-container *ngIf="isRunningRow(row); else staticRow">
            <app-timer-overlay
              *ngIf="expandedInlineTimer() && runningBlock() as runningBlock; else collapsedRunningRow"
              [id]="runningRowAnchorId"
              [runningBlock]="runningBlock"
              [activityName]="row.name"
              [activityLinkCommands]="['/today/allocation', row.activityTypeId]"
              [activityLinkQueryParams]="{ from: row.scheduleType }"
              [slotProgress]="store.getRunningAllocationProgressLabel()"
              [remainingSlots]="store.getRunningAllocationRemainingSlots()"
              [submitting]="blockMutationSubmitting()"
              [error]="blockMutationError()"
              (pause)="pauseRunningBlock()"
              (resume)="resumeRunningBlock()"
              (complete)="completeRunningBlock()"
              (autoAdvance)="onAutoAdvance()"
              (skip)="skipRunningBlock($event)"
              (hide)="collapseInlineTimer()"
              (refreshDayRequest)="refreshTimerError()">
            </app-timer-overlay>
            <ng-template #collapsedRunningRow>
              <div
                [id]="runningRowAnchorId"
                class="lm-row lm-row--running"
                [attr.aria-label]="row.name + ', ' + runningSummary()"
                role="button"
                tabindex="0"
                (click)="navigateToAllocation(row.activityTypeId)"
                (keydown.enter)="navigateToAllocation(row.activityTypeId)"
                (keydown.space)="navigateToAllocation(row.activityTypeId); $event.preventDefault()">
                <div class="lm-row__main">
                  <span class="lm-row__title">
                    <span>{{ row.name }}</span>
                    <span class="lm-menu-anchor" [attr.data-menu-anchor]="row.activityTypeId">
                      <button
                        type="button"
                        class="lm-icon-btn lm-icon-btn--ghost lm-icon-btn--inline"
                        [attr.data-menu-trigger]="row.activityTypeId"
                        [attr.aria-label]="'More actions for ' + row.name"
                        title="More"
                        [attr.aria-expanded]="isMenuOpen(row.activityTypeId)"
                        [attr.aria-haspopup]="'menu'"
                        (click)="openAllocationMenu($event, row.activityTypeId)">
                        <svg lucideEllipsis class="lm-icon lm-icon--sm" aria-hidden="true"></svg>
                      </button>
                      <ul *ngIf="isMenuOpen(row.activityTypeId)" class="lm-menu lm-menu--start" role="menu" [attr.aria-label]="'More actions for ' + row.name">
                        <li role="none">
                          <button
                            type="button"
                            class="lm-menu__item"
                            role="menuitem"
                            [attr.data-menu-item]="row.activityTypeId"
                            (click)="onOpenDetailsFromMenu($event, row.activityTypeId)">
                            <svg lucideChevronRight class="lm-icon" aria-hidden="true"></svg>
                            Open details
                          </button>
                        </li>
                      </ul>
                    </span>
                  </span>
                  <span class="lm-row__meta">
                    <span class="lm-dot" aria-hidden="true"></span>
                    {{ runningSummary() }}
                  </span>
                </div>
                <button
                  type="button"
                  class="lm-icon-btn"
                  data-inline-timer-expand
                  aria-label="Show timer"
                  title="Show timer"
                  (click)="stopRowNavigation($event); expandInlineTimer()">
                  <svg lucideMaximize2 class="lm-icon" aria-hidden="true"></svg>
                </button>
              </div>
            </ng-template>
          </ng-container>

          <ng-template #staticRow>
            <div
              class="lm-row"
              [class.lm-row--quiet]="row.doneThisCycle || row.notThisCycle"
              [attr.aria-label]="row.name"
              role="button"
              tabindex="0"
              (click)="navigateToAllocation(row.activityTypeId)"
              (keydown.enter)="navigateToAllocation(row.activityTypeId)"
              (keydown.space)="navigateToAllocation(row.activityTypeId); $event.preventDefault()">
              <div class="lm-row__main">
                <span class="lm-row__title">
                  <span>{{ row.name }}</span>
                  <span class="lm-menu-anchor" [attr.data-menu-anchor]="row.activityTypeId">
                    <button
                      type="button"
                      class="lm-icon-btn lm-icon-btn--ghost lm-icon-btn--inline"
                      [attr.data-menu-trigger]="row.activityTypeId"
                      [attr.aria-label]="'More actions for ' + row.name"
                      title="More"
                      [attr.aria-expanded]="isMenuOpen(row.activityTypeId)"
                      [attr.aria-haspopup]="'menu'"
                      (click)="openAllocationMenu($event, row.activityTypeId)">
                      <svg lucideEllipsis class="lm-icon lm-icon--sm" aria-hidden="true"></svg>
                    </button>
                    <ul *ngIf="isMenuOpen(row.activityTypeId)" class="lm-menu lm-menu--start" role="menu" [attr.aria-label]="'More actions for ' + row.name">
                      <li role="none">
                        <button
                          type="button"
                          class="lm-menu__item"
                          role="menuitem"
                          [attr.data-menu-item]="row.activityTypeId"
                          (click)="onOpenDetailsFromMenu($event, row.activityTypeId)">
                          <svg lucideChevronRight class="lm-icon" aria-hidden="true"></svg>
                          Open details
                        </button>
                      </li>
                      <li *ngIf="!row.notThisCycle && !row.doneThisCycle" role="none">
                        <button
                          type="button"
                          class="lm-menu__item"
                          role="menuitem"
                          (click)="onMarkNotThisCycleFromMenu($event, row.activityTypeId)">
                          <svg lucideBan class="lm-icon" aria-hidden="true"></svg>
                          Not this cycle
                        </button>
                      </li>
                    </ul>
                  </span>
                  <span *ngIf="row.doneThisCycle" class="lm-badge lm-badge--done">✓ Done</span>
                  <span *ngIf="row.notThisCycle" class="lm-badge">Not this cycle</span>
                </span>
                <span *ngIf="!row.notThisCycle" class="lm-row__meta">
                  <span>{{ row.completedSlots }} of {{ row.currentSlots }} slots</span>
                  <span aria-hidden="true">·</span>
                  <span>{{ getRowDurationLabel(row) }}</span>
                  <span aria-hidden="true">·</span>
                  <span
                    class="lm-days"
                    [class.lm-days--quiet]="row.daysSince === 0"
                    [attr.aria-label]="getDaysSinceAriaLabel(row)">
                    {{ row.daysSinceLabel }}
                  </span>
                </span>
                <span *ngIf="row.notThisCycle" class="lm-row__meta">{{ getSetAsideMetaLabel() }}</span>
              </div>
              <button
                *ngIf="row.notThisCycle"
                type="button"
                class="lm-btn"
                (click)="undoNotThisCycle($event, row.activityTypeId)"
                [disabled]="isNotThisCycleSubmitting(row.activityTypeId)">
                <svg lucideUndo2 class="lm-icon lm-icon--sm" aria-hidden="true"></svg>
                Undo
              </button>
              <button
                *ngIf="!row.notThisCycle && !row.doneThisCycle && getPausedBlockForActivity(row.activityTypeId); else startAction"
                type="button"
                class="lm-btn lm-btn--soft-go"
                (click)="resumePausedBlock($event, row.activityTypeId)"
                [disabled]="!canResumePausedBlock(row.activityTypeId)">
                Resume
              </button>
              <ng-template #startAction>
                <button
                  *ngIf="!row.notThisCycle && !row.doneThisCycle"
                  type="button"
                  class="lm-btn lm-btn--soft-go"
                  (click)="startBlock($event, row.activityTypeId)"
                  [disabled]="!canStartBlock(row.activityTypeId)">
                  {{ isStartSubmitting(row.activityTypeId) ? 'Starting…' : 'Start' }}
                </button>
              </ng-template>
            </div>
          </ng-template>
        </div>
      </div>
    </ng-container>
  </section>
</div>
